import type { FC, MouseEvent, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { delay } from '@/lib/motion'
import { smoothScrollTo } from '@/lib/smooth-scroll'
import { Container } from '@/components/generals/container'

export interface LegalSection {
    id: string
    title: string
    paragraphs: string[]
    list?: string[]
}

interface LegalDocumentProps {
    title: string
    intro: string
    updated: string
    sections: LegalSection[]
}

/** A calm long-form page with a sticky table of contents that follows the section being read. */
export const LegalDocument: FC<LegalDocumentProps> = ({ title, intro, updated, sections }): ReactNode => {
    const [current, setCurrent] = useState(sections[0]?.id ?? '')

    useEffect(() => {
        const elements = sections
            .map(section => document.getElementById(section.id))
            .filter((element): element is HTMLElement => element !== null)

        const observer = new IntersectionObserver(entries => {
            const visible = entries.filter(entry => entry.isIntersecting)

            if (visible.length === 0) return

            const topmost = visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]

            setCurrent(topmost.target.id)
        }, { rootMargin: '-20% 0px -65% 0px', threshold: 0 })

        elements.forEach(element => observer.observe(element))

        return () => observer.disconnect()
    }, [sections])

    const jump = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
        const target = document.getElementById(id)

        if (!target) return

        event.preventDefault()
        smoothScrollTo(target, -96)
        setCurrent(id)
        window.history.replaceState(null, '', `#${id}`)
    }

    return (
        <section id='document' className='pt-hero pb-14 sm:pb-[90px]'>
            <Container>
                <div className='grid gap-12 lg:grid-cols-[220px_1fr] lg:gap-16'>
                    <nav aria-label='On this page' className='hidden lg:block'>
                        <div className='sticky top-28'>
                            <p className='text-[12px] text-ink/50'>
                                On this page
                            </p>
                            <ol className='mt-4 flex flex-col gap-1 border-l border-ink/15'>
                                {sections.map((section, index) => {
                                    const active = section.id === current

                                    return (
                                        <li key={section.id}>
                                            <a
                                                href={`#${section.id}`}
                                                onClick={event => jump(event, section.id)}
                                                aria-current={active ? 'location' : undefined}
                                                className={cn(
                                                    '-ml-px flex gap-2.5 border-l py-1 pl-4 text-[13px] leading-snug transition-colors duration-300',
                                                    active ? 'border-ink font-medium text-ink' : 'border-transparent text-ink/55 hover:text-ink'
                                                )}
                                            >
                                                <span className='w-5 shrink-0 font-mono text-[11px] leading-[1.35rem] text-ink/40'>
                                                    {String(index + 1).padStart(2, '0')}
                                                </span>
                                                {section.title}
                                            </a>
                                        </li>
                                    )
                                })}
                            </ol>
                        </div>
                    </nav>

                    <div className='max-w-[640px]'>
                        <header>
                            <h1 className='type-hero enter' style={delay(0)}>
                                {title}
                            </h1>
                            <p className='type-lead enter mt-5' style={delay(100)}>
                                {intro}
                            </p>
                            <p className='enter mt-4 text-[13px] text-ink/55' style={delay(180)}>
                                Last updated {updated}
                            </p>
                        </header>

                        <div className='mt-12 border-t border-ink/15'>
                            {sections.map((section, index) => (
                                <section key={section.id} id={section.id} aria-labelledby={`${section.id}-title`} className='border-b border-ink/15 py-9 last:border-b-0'>
                                    <h2 id={`${section.id}-title`} className='flex items-baseline gap-3 font-serif text-[22px] leading-[1.35] tracking-[-0.025em] text-ink'>
                                        <span aria-hidden='true' className='font-mono text-[12px] text-ink/45'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                        {section.title}
                                    </h2>

                                    <div className='mt-4 flex flex-col gap-3.5 text-[15px] leading-[1.7] text-ink/80'>
                                        {section.paragraphs.map(paragraph => (
                                            <p key={paragraph}>
                                                {paragraph}
                                            </p>
                                        ))}

                                        {section.list && (
                                            <ul className='flex list-disc flex-col gap-2 pl-5 marker:text-ink/40'>
                                                {section.list.map(item => (
                                                    <li key={item}>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    )
}
